<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import FileService from '@/api/services/file.service';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import V2StoreMonogram from '@/components/v2/store-setup/shared/V2StoreMonogram.vue';
  import { useOnline } from '@vueuse/core';
  import { useUi } from '@/composition/use-ui';
  import {
    LOGO_ACCEPT,
    isAcceptedLogoFile,
    logoUploadName,
  } from '@/domain/store-setup/logo-file';
  import { AlertType } from '@/interfaces/IAlert';
  import { prepareLogoImage } from '@/lib/store-setup/logo-image';
  import { Globals } from '@/modules/Globals';

  /**
   * «Logo (opcional)» (plan §4.2 item 6, D10): a 72 px tile with the logo or
   * the initials, «Subir logo» / «Cambiar logo» and «Quitar». The image is
   * resized in the browser and uploaded to a temporary path the create call
   * hands to the server; no crop. `logo-color` reports the upload's dominant
   * colour (null without logo or colour), which the preview paints (D-ADM-5).
   */
  const TILE_SIZE = 72;

  const props = withDefaults(
    defineProps<{
      /** The uploaded `temp/…` path, or null. */
      value?: string | null;
      initials: string;
      disabled?: boolean;
    }>(),
    { value: null, disabled: false }
  );
  const emit = defineEmits<{
    (e: 'input', path: string | null): void;
    (e: 'logo-color', color: string | null): void;
    (e: 'uploading', uploading: boolean): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const ui = useUi();
  const online = useOnline();

  const fileInput = ref<HTMLInputElement | null>(null);
  const uploading = ref(false);
  /** The image failed to load: the initials stand in. */
  const imageFailed = ref(false);
  let alive = true;

  const imageUrl = computed(() =>
    props.value && !imageFailed.value
      ? `${Globals.uploadsPath}/${props.value}`
      : null
  );
  /** Picking needs the network (the upload starts right away). */
  const pickDisabled = computed(
    () => props.disabled || uploading.value || !online.value
  );

  function setUploading(value: boolean) {
    uploading.value = value;
    emit('uploading', value);
  }

  function choose() {
    if (pickDisabled.value) return;
    fileInput.value?.click();
  }

  async function upload(file: File) {
    setUploading(true);
    try {
      const blob = await prepareLogoImage(file);
      const entity = await FileService.upload(
        new File([blob], logoUploadName(blob.type), {
          type: blob.type || 'image/jpeg',
        })
      );
      if (!alive) return;
      emit('input', entity.path);
      emit('logo-color', entity.dominantColor ?? null);
    } catch (error) {
      console.error('[store-setup] logo upload failed', error);
      if (alive)
        ui.showToast(AlertType.Error, tr('storeSetup.business.logoError'));
    } finally {
      if (alive) setUploading(false);
    }
  }

  function onFileChange(event: Event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const file = input.files?.[0] ?? null;
    // Picking the same file again must fire `change` again.
    input.value = '';
    if (!file) return;
    if (!isAcceptedLogoFile(file)) {
      ui.showToast(AlertType.Error, tr('storeSetup.business.logoWrongFile'));
      return;
    }
    void upload(file);
  }

  function remove() {
    if (props.disabled || uploading.value) return;
    emit('input', null);
    emit('logo-color', null);
  }

  watch(
    () => props.value,
    () => {
      imageFailed.value = false;
    }
  );

  onBeforeUnmount(() => {
    alive = false;
    // The page must not wait for an upload nobody will finish.
    if (uploading.value) emit('uploading', false);
  });
</script>

<template>
  <div class="v2-setup-logo-tile">
    <p class="logo-label">{{ $t('storeSetup.business.logo') }}</p>
    <div class="logo-row">
      <button
        type="button"
        class="logo-tile"
        :class="{ 'is-busy': uploading }"
        tabindex="-1"
        aria-hidden="true"
        :disabled="pickDisabled"
        @click="choose"
      >
        <span v-if="uploading" class="tile-spinner" />
        <img
          v-else-if="imageUrl"
          class="tile-image"
          :src="imageUrl"
          alt=""
          @error="imageFailed = true"
        />
        <V2StoreMonogram v-else :initials="initials" :size="TILE_SIZE" />
      </button>
      <div class="logo-side">
        <div class="logo-actions">
          <PosUiButton
            color="neutral"
            variant="outline"
            size="sm"
            icon="upload"
            class="logo-upload"
            :disabled="pickDisabled"
            @click="choose"
            >{{
              value
                ? $t('storeSetup.business.logoChange')
                : $t('storeSetup.business.logoUpload')
            }}</PosUiButton
          >
          <button
            v-if="value"
            type="button"
            class="logo-remove"
            :disabled="disabled || uploading"
            @click="remove"
          >
            {{ $t('storeSetup.business.logoRemove') }}
          </button>
        </div>
        <p class="logo-hint">
          {{ $t('storeSetup.business.logoHint', { initials }) }}
        </p>
      </div>
    </div>
    <input
      ref="fileInput"
      class="logo-input"
      type="file"
      :accept="LOGO_ACCEPT"
      tabindex="-1"
      aria-hidden="true"
      @change="onFileChange"
    />
  </div>
</template>

<style scoped lang="scss">
  .v2-setup-logo-tile {
    @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-2;
    .logo-label {
      @apply tw-m-0 tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    .logo-row {
      @apply tw-flex tw-items-center tw-gap-4;
    }
    .logo-tile {
      @apply tw-flex tw-h-[72px] tw-w-[72px] tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-md tw-border-0 tw-bg-neutral-100 tw-p-0;
      &:disabled {
        @apply tw-cursor-default;
      }
      &.is-busy {
        @apply tw-border tw-border-dashed tw-border-neutral-300;
      }
    }
    .tile-image {
      @apply tw-h-full tw-w-full tw-object-cover;
    }
    .tile-spinner {
      @apply tw-h-6 tw-w-6 tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-neutral-400 tw-border-t-transparent;
    }
    .logo-side {
      @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-1;
    }
    .logo-actions {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1;
    }
    .logo-upload {
      @apply tw-min-h-11;
    }
    .logo-remove {
      @apply tw-min-h-11 tw-border-0 tw-bg-transparent tw-px-1 tw-text-sm tw-font-medium tw-text-neutral-700 tw-underline tw-underline-offset-4;
      &:disabled {
        @apply tw-cursor-not-allowed tw-text-neutral-400 tw-no-underline;
      }
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .logo-hint {
      @apply tw-m-0 tw-text-xs tw-text-neutral-500;
    }
    .logo-input {
      @apply tw-hidden;
    }
  }
</style>
